import {
  Badge,
  Box,
  Button,
  CheckIcon,
  CloseIcon,
  Divider,
  Drawer,
  DrawerBody,
  DrawerHeader,
  InfoGroup,
  InfoItem,
  InfoItemKey,
  InfoItemValue,
  RefreshIcon,
  Spinner,
  Text,
} from '@razorpay/blade/components';
import { useDetailsView, useSalaryComponentHelpers } from 'components/SalaryComponents/hooks';
import { ActionModals } from 'components/SalaryComponents/modals';
import { salaryComponentParams } from '../../constants';

import React, { useState } from 'react';
import { EmptyView } from '../../components/EmptyView';
import { CheckIconWrapper } from './Create/styles';
import { formatBenefitAmount, payrollMonthLabel } from './utils';
import useFeature from 'hooks/useFeature';
import { useQuery } from 'react-query';
import api from 'api';
import { flexibleBenefitsTaxAllSections } from 'components/WizardViews/SalaryComponents/constants';
import { overlayLayers } from 'constants/overlayLayers.generated';

export const FlexibleBenefitsDetailView = () => {
  const { paramId: salaryComponentId, closeDetailsView } = useDetailsView(
    salaryComponentParams.componentId,
  );
  const { openFlexibleBenefitsEditPage } = useSalaryComponentHelpers();
  const { isFbpPercentagePrecisionEnabled } = useFeature('fbpPercentagePrecision');
  const [currentActiveModal, setCurrentActiveModal] = useState<string | null>(null);
  const isOpen = !!salaryComponentId;
  const { isBaseCompensationFloorEnabled } = useFeature('baseCompensationFloor');
  const { isFbpExcludeFromSaAdjustmentEnabled } = useFeature('fbpExcludeFromSaAdjustment');

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: [
      api.flexibleBenefitComponents.queries.getFlexibleBenefit,
      salaryComponentId,
      isFbpPercentagePrecisionEnabled,
    ],
    queryFn: () =>
      api.flexibleBenefitComponents.queries.getFlexibleBenefit(
        salaryComponentId!,
        isFbpPercentagePrecisionEnabled,
      ),
    enabled: isOpen,
    select: (data) => {
      return data?.payrollGetFlexibleBenefitsComponent?.component;
    },
  });

  const openModal = (modal: string) => {
    setCurrentActiveModal(modal);
  };

  const closeModal = () => {
    setCurrentActiveModal(null);
  };

  const onDismiss = () => {
    closeModal();
    closeDetailsView();
  };

  const handleEditClick = () => {
    openFlexibleBenefitsEditPage(salaryComponentId!);
  };

  const isModifyDisabled = () => {
    return data?.component_space === 'tenant';
  };

  const {
    tax_behaviour,
    proration_behaviour,
    arrear_behaviour,
    salary_settings,
    declaration_settings,
    hold_settings,
    display_behaviour,
    benefit_amount_settings,
    pf_behaviour,
    esi_behaviour,
    pt_behaviour,
    lwf_behaviour,
    gratuity_behaviour,
    leave_encashment_behaviour,
    ot_behaviour,
    base_compensation_behaviour,
    sa_adjustment_settings,
  } = data?.settings || {};

  const getDeclarationMode = () => {
    return declaration_settings?.declaration_enabled ? 'Allow declaration' : 'No declaration';
  };

  const getBenefitAmount = () => {
    const { displayValue, description, lines } = formatBenefitAmount(
      benefit_amount_settings,
      isFbpPercentagePrecisionEnabled ? data?.response_precision : undefined,
    );
    if (displayValue === '--') {
      return '-';
    }
    if (lines && lines.length > 0) {
      return (
        <Box display="flex" flexDirection="column">
          {description && (
            <Text size="small" color={'surface.text.gray.muted'}>
              {description}
            </Text>
          )}
          {lines.map((line, index) => (
            <Text key={index}>{line}</Text>
          ))}
        </Box>
      );
    }
    return description ? `${description} - ${displayValue}` : displayValue;
  };

  const getIncludeInGrossSalary = () => {
    return salary_settings?.show_in_gross_salary
      ? 'Add to Gross Salary'
      : 'Do not add to Gross Salary';
  };

  const getBenefitType = () => {
    return salary_settings?.non_cash_benefit ? 'Non-cash benefit' : 'Cash benefit';
  };

  const getUnclaimedAmount = () => {
    return hold_settings?.hold_unclaimed_amount ? 'Hold amount' : 'Do not hold';
  };

  const getSaAdjustment = () => {
    return sa_adjustment_settings?.adjust_to_sa === false ? 'No' : 'Yes';
  };

  const getTaxCalculationBasis = () => {
    return tax_behaviour?.tax_calculation_basis?.basis === 'declared_amount_by_employee'
      ? 'Amount declared by the Employee'
      : 'Amount approved by the Employer';
  };

  return (
    <>
      <Drawer isOpen={isOpen} onDismiss={onDismiss} zIndex={overlayLayers.base}>
        {isLoading && (
          <DrawerBody>
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="200px"
              gap="spacing.5">
              <Spinner accessibilityLabel="loading flexible benefits component details" />
              <Text>Loading details</Text>
            </Box>
          </DrawerBody>
        )}
        {isError && (
          <DrawerBody>
            <EmptyView
              description={
                <Text color={'surface.text.gray.muted'} weight={'medium'} textAlign={'center'}>
                  Due to an unknown error, we could not load the component details page.
                </Text>
              }>
              <Box display={'flex'} justifyContent={'center'}>
                <Box width={'104px'}>
                  <Button icon={RefreshIcon} size={'small'} onClick={() => refetch()}>
                    Retry
                  </Button>
                </Box>
              </Box>
            </EmptyView>
          </DrawerBody>
        )}
        {data && !isLoading && !isError && (
          <DrawerHeader
            title={data.name}
            titleSuffix={
              <Badge color={data.is_active ? 'positive' : 'neutral'}>
                {data.is_active ? 'Active' : 'Disabled'}
              </Badge>
            }
            leading={null}>
            <Text size="small" color="surface.text.gray.muted" marginTop="spacing.2">
              {data?.description || ''}
            </Text>
          </DrawerHeader>
        )}
        {data && !isLoading && !isError && (
          <DrawerBody>
            <Box display="flex" flexDirection="column" gap="spacing.6" marginBottom={'80px'}>
              <Box display="flex" flexDirection="column" gap="spacing.4">
                <Text size="medium" weight="semibold" color="surface.text.gray.subtle">
                  Basic
                </Text>
                <Box display="flex" justifyContent={'space-between'}>
                  <Text size="medium" weight="semibold" color="surface.text.gray.muted">
                    Display name
                  </Text>
                  <Text size="medium" weight="semibold" color="surface.text.gray.subtle">
                    {display_behaviour?.display_name || data.name || ''}
                  </Text>
                </Box>
                <InfoGroup valueAlign="right" itemOrientation="horizontal" size="medium">
                  <InfoItem>
                    <InfoItemKey>Include in Gross salary</InfoItemKey>
                    <InfoItemValue>{getIncludeInGrossSalary()}</InfoItemValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoItemKey>Benefit type</InfoItemKey>
                    <InfoItemValue>{getBenefitType()}</InfoItemValue>
                  </InfoItem>
                  {isFbpExcludeFromSaAdjustmentEnabled && (
                    <InfoItem>
                      <InfoItemKey>Adjust from Special Allowance</InfoItemKey>
                      <InfoItemValue>{getSaAdjustment()}</InfoItemValue>
                    </InfoItem>
                  )}
                  {isFbpExcludeFromSaAdjustmentEnabled &&
                    sa_adjustment_settings?.adjust_to_sa === false && (
                      <InfoItem>
                        <InfoItemKey>Tax calculation basis</InfoItemKey>
                        <InfoItemValue>{getTaxCalculationBasis()}</InfoItemValue>
                      </InfoItem>
                    )}
                  {isFbpExcludeFromSaAdjustmentEnabled &&
                    sa_adjustment_settings?.adjust_to_sa === false &&
                    tax_behaviour?.tax_calculation_basis?.basis ===
                      'declared_amount_by_employee' && (
                      <InfoItem>
                        <InfoItemKey>Unclaimed amounts taxable from</InfoItemKey>
                        <InfoItemValue>
                          {payrollMonthLabel(
                            tax_behaviour?.tax_calculation_basis?.unclaimed_taxable_from_month,
                          )}
                        </InfoItemValue>
                      </InfoItem>
                    )}
                </InfoGroup>
              </Box>

              <Divider />

              <Box display="flex" flexDirection="column" gap="spacing.4">
                <Text size="medium" weight="semibold" color="surface.text.gray.subtle">
                  Amount
                </Text>
                <InfoGroup itemOrientation="horizontal" size="medium" valueAlign="right">
                  <InfoItem>
                    <InfoItemKey>Declaration mode</InfoItemKey>
                    <InfoItemValue>{getDeclarationMode()}</InfoItemValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoItemKey>Benefit amount</InfoItemKey>
                    <InfoItemValue>{getBenefitAmount()}</InfoItemValue>
                  </InfoItem>
                </InfoGroup>
              </Box>

              <Divider />

              <Box display="flex" flexDirection="column" gap="spacing.4">
                <Text size="medium" weight="semibold" color="surface.text.gray.subtle">
                  Tax
                </Text>
                <InfoGroup itemOrientation="horizontal" size="medium" valueAlign="right">
                  <InfoItem>
                    <InfoItemKey>Exempted - Old Regime</InfoItemKey>
                    <InfoItemValue>
                      <CheckIconWrapper
                        type={tax_behaviour?.tax_exempt_in_old_regime ? 'positive' : 'negative'}>
                        {tax_behaviour?.tax_exempt_in_old_regime ? (
                          <CheckIcon color={'feedback.icon.positive.intense'} />
                        ) : (
                          <CloseIcon color="feedback.icon.negative.intense" />
                        )}
                      </CheckIconWrapper>
                    </InfoItemValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoItemKey>Exempted - New Regime</InfoItemKey>
                    <InfoItemValue>
                      <CheckIconWrapper
                        type={tax_behaviour?.tax_exempt_in_new_regime ? 'positive' : 'negative'}>
                        {tax_behaviour?.tax_exempt_in_new_regime ? (
                          <CheckIcon color={'feedback.icon.positive.intense'} />
                        ) : (
                          <CloseIcon color="feedback.icon.negative.intense" />
                        )}
                      </CheckIconWrapper>
                    </InfoItemValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoItemKey>Exemption under Section</InfoItemKey>
                    <InfoItemValue>
                      {flexibleBenefitsTaxAllSections.find(
                        (section) => section.key === tax_behaviour?.tax_exemption_section,
                      )?.label || '-'}
                    </InfoItemValue>
                  </InfoItem>

                  <InfoItem>
                    <InfoItemKey>Proration</InfoItemKey>
                    <InfoItemValue>
                      {proration_behaviour?.support_proration ? 'Enabled' : 'Disabled'}
                    </InfoItemValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoItemKey>Arrears calculation</InfoItemKey>
                    <InfoItemValue>
                      {arrear_behaviour?.consider_in_arrear ? 'Enabled' : 'Disabled'}
                    </InfoItemValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoItemKey>Unclaimed amount</InfoItemKey>
                    <InfoItemValue>{getUnclaimedAmount()}</InfoItemValue>
                  </InfoItem>
                </InfoGroup>
              </Box>

              <Divider />

              {(salary_settings?.show_in_gross_salary || isBaseCompensationFloorEnabled) && (
                <Box display="flex" flexDirection="column" gap="spacing.4">
                  <Text size="medium" weight="semibold" color="surface.text.gray.subtle">
                    Wage calculation
                  </Text>
                  <InfoGroup itemOrientation="horizontal" size="medium" valueAlign="right">
                    {salary_settings?.show_in_gross_salary && (
                      <>
                        <InfoItem>
                          <InfoItemKey>Part of PF wage</InfoItemKey>
                          <InfoItemValue>
                            {pf_behaviour?.include_in_pf_wages ? 'Yes' : 'No'}
                          </InfoItemValue>
                        </InfoItem>
                        <InfoItem>
                          <InfoItemKey>Part of ESI wage</InfoItemKey>
                          <InfoItemValue>
                            {esi_behaviour?.include_in_esi_wages ? 'Yes' : 'No'}
                          </InfoItemValue>
                        </InfoItem>
                        <InfoItem>
                          <InfoItemKey>Part of PT wage</InfoItemKey>
                          <InfoItemValue>
                            {pt_behaviour?.include_in_pt_wages ? 'Yes' : 'No'}
                          </InfoItemValue>
                        </InfoItem>
                        <InfoItem>
                          <InfoItemKey>Part of LWF wage</InfoItemKey>
                          <InfoItemValue>
                            {lwf_behaviour?.include_in_lwf_wages ? 'Yes' : 'No'}
                          </InfoItemValue>
                        </InfoItem>
                        <InfoItem>
                          <InfoItemKey>Part of Gratuity wages</InfoItemKey>
                          <InfoItemValue>
                            {gratuity_behaviour?.include_in_gratuity_wages ? 'Yes' : 'No'}
                          </InfoItemValue>
                        </InfoItem>
                        <InfoItem>
                          <InfoItemKey>Part of Leave Encashment wages</InfoItemKey>
                          <InfoItemValue>
                            {leave_encashment_behaviour?.include_in_leave_encashment_wages
                              ? 'Yes'
                              : 'No'}
                          </InfoItemValue>
                        </InfoItem>
                        <InfoItem>
                          <InfoItemKey>Part of OT wage</InfoItemKey>
                          <InfoItemValue>
                            {ot_behaviour?.include_in_ot_wages ? 'Yes' : 'No'}
                          </InfoItemValue>
                        </InfoItem>
                      </>
                    )}
                    {isBaseCompensationFloorEnabled && (
                      <InfoItem>
                        <InfoItemKey>Part of Base Compensation</InfoItemKey>
                        <InfoItemValue>
                          {base_compensation_behaviour?.include_in_base_compensation ? 'Yes' : 'No'}
                        </InfoItemValue>
                      </InfoItem>
                    )}
                  </InfoGroup>
                </Box>
              )}

              <Box
                position={'absolute'}
                bottom={'spacing.0'}
                backgroundColor={'surface.background.gray.intense'}
                width={'calc(100% - 32px)'}
                paddingX="spacing.0"
                paddingY="spacing.6">
                <Box display="flex" flexDirection="row" gap="spacing.4">
                  <Button isFullWidth onClick={handleEditClick} isDisabled={isModifyDisabled()}>
                    Modify
                  </Button>
                  <Button
                    isFullWidth
                    variant="tertiary"
                    onClick={() => openModal(data.is_active ? 'disable' : 'enable')}>
                    {data.is_active ? 'Disable' : 'Enable'}
                  </Button>
                </Box>
              </Box>
            </Box>
          </DrawerBody>
        )}
      </Drawer>
      {data ? (
        <ActionModals
          salaryComponent={data}
          currentActiveModal={currentActiveModal}
          closeModal={closeModal}
          enableModalContent={
            <Box display={'flex'} flexDirection={'column'} gap={'spacing.5'}>
              <Box display={'flex'} flexDirection={'column'} gap={'spacing.2'}>
                <Text size="small" color={'surface.text.gray.muted'} weight={'medium'}>
                  What will happen?
                </Text>
                <Text color={'surface.text.gray.subtle'}>
                  Once enabled, you can add the component to an active employee's compensation
                </Text>
              </Box>
            </Box>
          }
          disableModalContent={
            <Box display={'flex'} flexDirection={'column'} gap={'spacing.5'}>
              <Box display={'flex'} flexDirection={'column'} gap={'spacing.2'}>
                <Text size="small" color={'surface.text.gray.muted'} weight={'medium'}>
                  What will happen?
                </Text>
                <Text color={'surface.text.gray.subtle'}>
                  Once enabled, it can be used to employee to save tax
                </Text>
              </Box>
              <Box display={'flex'} flexDirection={'column'} gap={'spacing.2'}>
                <Text size="small" color={'surface.text.gray.muted'} weight={'medium'}>
                  When can a component be disabled?
                </Text>
                <Text color={'surface.text.gray.subtle'}>
                  This can be removed. A flexi component can always be disabled
                </Text>
              </Box>
            </Box>
          }
        />
      ) : null}
    </>
  );
};
